// src/pages/products/EditProductPage/components/ImagesTab.jsx
import React, { useState, useRef } from 'react';
import { Upload, X, Eye, RotateCcw, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import styles from '../styles/ImagesTab.module.css';

const ImagesTab = ({
  images = [], // Array of image objects: { id?, file?, url?, isNew?, isModified?, isExisting? }
  mainImageIndex = 0,
  onImageUpload,
  onImageRemove,
  onSetMainImage,
  onImageReplace,
  onImageRevert, // For reverting changes to existing images
  onDragOver,
  onDrop,
  errors = {},
  maxImages = 5,
  isEdit = false, // Whether we're editing an existing product
  existingImages = [] // Original images for comparison
}) => {
  const { t } = useLocalization();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRefs = useRef([]);

  // Initialize file input refs
  React.useEffect(() => {
    fileInputRefs.current = fileInputRefs.current.slice(0, maxImages);
  }, [maxImages]);

  const handleFileSelect = (index, file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      console.error('Invalid file type:', file.type);
      return;
    }

    // Validate file size (e.g., 5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      console.error('File too large:', file.size);
      return;
    }

    if (images[index]) {
      // Replacing existing image
      onImageReplace?.(index, file);
    } else {
      // Adding new image
      onImageUpload?.(index, file);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOverSlot = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(e);
  };

  const handleDropOnSlot = (e, index) => {
    e.preventDefault();
    
    // Handle file drops
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleFileSelect(index, files[0]);
      return;
    }

    // Handle image reordering
    if (draggedIndex !== null && draggedIndex !== index) {
      // Implement reordering logic here if needed
      // onImageReorder?.(draggedIndex, index);
    }
    
    onDrop?.(e, index);
    setDraggedIndex(null);
  };

  const getImageSrc = (image, index) => {
    if (!image) return null;
    
    // New file upload or modified existing image
    if (image.file && (image.isNew || image.isModified)) {
      return URL.createObjectURL(image.file);
    }
    
    // Existing image URL
    if (image.url) {
      return image.url;
    }
    
    // Fallback for direct file objects (backward compatibility)
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }
    
    return null;
  };

  const isImageModified = (image, index) => {
    if (!isEdit || !image) return false;
    return image.isModified || image.isNew;
  };

  const canRevertImage = (image, index) => {
    if (!isEdit || !image) return false;
    return image.isModified && existingImages[index];
  };

  const handleRevertImage = (index) => {
    onImageRevert?.(index);
  };

  const triggerFileInput = (index) => {
    fileInputRefs.current[index]?.click();
  };

  const renderImageSlot = (image, index) => {
    const imageSrc = getImageSrc(image, index);
    const isMain = index === mainImageIndex;
    const isModified = isImageModified(image, index);
    const canRevert = canRevertImage(image, index);
    const isDraggedOver = draggedIndex === index;

    return (
      <div
        key={index}
        className={`${styles.imageUploadSlot} ${
          index === 0 ? styles.mainSlot : ''
        } ${isMain ? styles.mainImageSlot : ''} ${
          isDraggedOver ? styles.draggedOver : ''
        } ${isModified ? styles.modifiedSlot : ''}`}
        onDragOver={(e) => handleDragOverSlot(e, index)}
        onDrop={(e) => handleDropOnSlot(e, index)}
        draggable={!!image}
        onDragStart={(e) => handleDragStart(e, index)}
        onDragEnd={handleDragEnd}
      >
        {imageSrc ? (
          <div className={styles.imagePreview}>
            <img 
              src={imageSrc} 
              alt={`Product ${index + 1}`} 
              className={styles.previewImage}
              onError={(e) => {
                console.error('Image failed to load:', e);
                // Handle image load error
                e.target.style.display = 'none';
              }}
            />
            <div className={styles.imageActions}>
              <button
                type="button"
                className={styles.removeImageButton}
                onClick={() => onImageRemove?.(index)}
                title={t('products.removeImage')}
              >
                <X size={12} />
              </button>
              
              <button
                type="button"
                className={styles.replaceImageButton}
                onClick={() => triggerFileInput(index)}
                title={t('products.replaceImage')}
              >
                <Upload size={12} />
              </button>
              
              {!isMain && (
                <button
                  type="button"
                  className={styles.setMainButton}
                  onClick={() => onSetMainImage?.(index)}
                  title={t('products.setAsMainImage')}
                >
                  <Eye size={12} />
                </button>
              )}
              
              {canRevert && (
                <button
                  type="button"
                  className={styles.revertButton}
                  onClick={() => handleRevertImage(index)}
                  title={t('products.revertImage')}
                >
                  <RotateCcw size={12} />
                </button>
              )}
            </div>
            
            {isMain && (
              <div className={styles.mainImageBadge}>
                {t('products.mainImageBadge')}
              </div>
            )}
            
            {isModified && (
              <div className={styles.modifiedBadge}>
                <AlertCircle size={10} />
                {t('products.modified')}
              </div>
            )}
            
            <input
              ref={(el) => (fileInputRefs.current[index] = el)}
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={(e) => handleFileSelect(index, e.target.files[0])}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div 
            className={styles.uploadPlaceholder}
            onClick={() => triggerFileInput(index)}
          >
            <input
              ref={(el) => (fileInputRefs.current[index] = el)}
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={(e) => handleFileSelect(index, e.target.files[0])}
              className={styles.imageInput}
            />
            <Upload size={20} className={styles.uploadIcon} />
            <span className={styles.uploadText}>
              {index === 0 ? t('products.uploadMainImage') : t('products.uploadImages')}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Create array of slots (existing images + empty slots)
  const imageSlots = Array.from({ length: maxImages }, (_, index) => 
    images[index] || null
  );

  return (
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.productImages')}</h2>
        <p>{t('products.productImagesRecommendation')}</p>
        {isEdit && (
          <p className={styles.editModeNote}>
            {t('products.editModeImageNote')}
          </p>
        )}
      </div>
      
      <div className={styles.cardContent}>
        <div className={styles.imageUploadSection}>
          <div className={styles.imageUploadGrid}>
            {imageSlots.map((image, index) => renderImageSlot(image, index))}
          </div>
          
          <div className={styles.uploadInstructions}>
            <p>{t('products.dragDropInstructions')}</p>
            <p>{t('products.maxImagesNote', { max: maxImages })}</p>
            {isEdit && (
              <p className={styles.editInstructions}>
                {t('products.editModeInstructions')}
              </p>
            )}
          </div>
          
          {errors.images && (
            <span className={styles.errorText}>{errors.images}</span>
          )}
          
          {errors.imageSize && (
            <span className={styles.errorText}>{errors.imageSize}</span>
          )}
          
          {errors.imageFormat && (
            <span className={styles.errorText}>{errors.imageFormat}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagesTab;